import * as config from '../../../wips.config.js';
import { ContextService } from '../../../services/context_service';
import { lazyInject } from '../../../services/inversify';
import { MehrsprachigkeitFaq } from './mehrsprachigkeit/MehrsprachigkeitFaq';
import { MehrsprachigkeitFormulare } from './mehrsprachigkeit/MehrsprachigkeitFormulare';
import { MehrsprachigkeitGlossar } from './mehrsprachigkeit/MehrsprachigkeitGlossar';
import { MehrsprachigkeitNavigation } from './mehrsprachigkeit/MehrsprachigkeitNavigation';
import { MehrsprachigkeitOnlineHilfe } from './mehrsprachigkeit/MehrsprachigkeitOnlineHilfe';
import { MehrsprachigkeitStatischeInhalte } from './mehrsprachigkeit/MehrsprachigkeitStatischeInhalte';
import { MehrsprachigkeitSystemtexte } from './mehrsprachigkeit/MehrsprachigkeitSystemtexte';
import * as React from 'react';
import { Routing } from '../../../helpers/Routing';
import { RoutingIDs } from '../../../helpers/RoutingAdminIDs';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

interface DatenverwaltungTabMehrsprachigkeitProps extends React.ClassAttributes<any> {
    data: any;
    onRoutingChange: (routing: any) => void;
    routing: any;
    standortSelected: any;
}

interface DatenverwaltungTabMehrsprachigkeitState {
    tabIndex: number;
}

export class DatenverwaltungTabMehrsprachigkeit extends
    React.Component<DatenverwaltungTabMehrsprachigkeitProps, DatenverwaltungTabMehrsprachigkeitState> {

    constructor(props: any) {
        super(props);
        this.state = {
            tabIndex: null
        };
    }

    @lazyInject(ContextService) private contextService: ContextService;

    private devFeaturesMap: any = config.devFeaturesMap || {};
    private disabled: string[] = [];
    private prefix: string = 'admin.tab.datenverwaltung.mehrsprachigkeit';
    private rechte: any = {};
    private tabName: string = RoutingIDs.TABS_DATENVERWALTUNG_MEHRSPRACHIGKEIT;
    private tabIndex: string = `${this.tabName}Index`;
    private tabRoutings: any[] = [
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_NAVIGATION },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_FORMULARE },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_STATISCHE_INHALTE },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_FAQ },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_GLOSSAR },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_SYSTEMTEXTE },
        { path: RoutingIDs.ROUTE_MEHRSPRACHIGKEIT_ONLINE_HILFE }
    ];
    private tabs: string[] = [ // Reitereihenfolge beachten
        'navigation', 'formulare', 'statische_inhalte', 'faq', 'glossar', 'systemtexte', 'online_hilfe'
    ];

    componentDidMount(): void {
        if (!this.devFeaturesMap.N214_FAQ) {
            this.disabled = [...this.disabled, 'faq'];
        }
        (new Routing(this)).init((rechte: any, tabIndex: number) => {
            this.rechte = rechte || {};
            this.setTabIndex(tabIndex, this.tabRoutings[tabIndex]);
        });
    }

    componentDidUpdate(prevProps: any): void {
        if (JSON.stringify(this.props.routing) !== JSON.stringify(prevProps.routing)) {
            this.checkRouting();
        }
    }

    checkRouting(): void {
        const routing: any = this.props.routing || {};
        if (typeof (routing.path || {})[this.tabName] === 'undefined') {
            return;
        }
        if (this.state.tabIndex === routing.path[this.tabName]) {
            if (routing.path.terminateOn === this.tabName) {
                this.props.onRoutingChange({});
            }
        } else {
            this.setTabIndex(routing.path[this.tabName], routing);
        }
    }

    render(): JSX.Element {
        if (this.state.tabIndex === null) {
            return null;
        }

        return (
            <Tabs
                className='tabs-admin-datenverwaltung'
                selectedIndex={this.state.tabIndex}
                onSelect={(tabIndex, lastIndex, event) => this.setTabIndex(tabIndex, this.tabRoutings[tabIndex])}
            >
                <TabList>
                    <Tab disabled={!this.rechte.navigation}>Navigation</Tab>
                    <Tab disabled={!this.rechte.formulare}>Formulare</Tab>
                    <Tab disabled={!this.rechte.statische_inhalte}>Statische Inhalte</Tab>
                    <Tab disabled={!this.rechte.faq}>FAQ</Tab>
                    <Tab disabled={!this.rechte.glossar}>Glossar</Tab>
                    <Tab disabled={!this.rechte.systemtexte}>Systemtexte</Tab>
                    <Tab disabled={!this.rechte.online_hilfe}>Online-Hilfe</Tab>
                </TabList>
                <TabPanel>
                    {this.rechte.navigation &&
                        <MehrsprachigkeitNavigation
                            data={this.props.data}
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.formulare &&
                        <MehrsprachigkeitFormulare
                            data={this.props.data}
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.statische_inhalte &&
                        <MehrsprachigkeitStatischeInhalte
                            data={this.props.data}
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.faq &&
                        <MehrsprachigkeitFaq
                            admin
                            data={this.props.data}
                            isMultilingual
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.glossar &&
                        <MehrsprachigkeitGlossar
                            admin
                            data={this.props.data}
                            isMultilingual
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.systemtexte &&
                        <MehrsprachigkeitSystemtexte
                            admin
                            data={this.props.data}
                            isMultilingual
                        />
                    }
                </TabPanel>
                <TabPanel>
                    {this.rechte.online_hilfe &&
                        <MehrsprachigkeitOnlineHilfe
                            data={this.props.data}
                        />
                    }
                </TabPanel>
            </Tabs>
        );
    }

    setTabIndex(tabIndex: number, tabRouting: any, callback?: () => void): void {
        if (this.state.tabIndex !== tabIndex) {
            setTimeout(() => {
                this.setState({ tabIndex }, () => {
                    this.props.onRoutingChange(tabRouting);
                    this.contextService.set(this.tabIndex, tabIndex);

                    if (typeof callback === 'function') {
                        callback();
                    }
                });
            });
        }
    }
}
